import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import i18next from 'i18next';
import { toast } from 'react-toastify';

import SimpleTableWithSorting, {
  TableCellProps,
  generateColumn,
  getRTOriginal,
} from '../common/SimpleTableWithSorting';
import { NormalizedRestaurant } from 'types/restaurants';
import { User } from 'types/user';
import Shapes from 'shapes/main';
import OtoButtons from '../common/OtoButtons';
import APIService from 'services/api';
import { LOG_BAG } from 'utils/log';
import { YesNoIcons } from '../icons/IconSets';

interface IProps {
  canEditDrivers: boolean;
  drivers: User[];
  onDelete: (driver: User) => void;
  restaurant: NormalizedRestaurant;
}

const DriversList = (props: IProps) => {
  const { drivers, canEditDrivers, onDelete, restaurant } = props;

  const deleteDriver = useCallback(
    (driver: User) => {
      return APIService.del(
        `/restaurants/${restaurant.id}/drivers/${driver.id}`
      )
        .then(() => {
          toast.success(i18next.t('drivers-list.driver-deleted', { name: driver.name }));
          onDelete(driver);
          return driver;
        })
        .catch((error: Error) => {
          toast.error(i18next.t('drivers-list.driver-delete-error'));
          LOG_BAG.logError('Driver delete error', error);
        });
    },
    [onDelete, restaurant]
  );

  const columns = useMemo(
    () => [
      generateColumn('name'),
      generateColumn('email'),
      ...(canEditDrivers
        ? [
            generateColumn('is_working', {
              customCellFormatter: (props: TableCellProps<User>) =>
                getRTOriginal<User>(props)?.is_working
                  ? YesNoIcons.YES
                  : YesNoIcons.NO,
            }),
          ]
        : []),
      generateColumn('id', {
        customHeader: i18next.t('drivers-list.actions-column'),
        customCellFormatter: (props: TableCellProps<User>) => (
          <>
            <OtoButtons.DeleteButton
              onClick={() => deleteDriver(getRTOriginal(props))}
            />
          </>
        ),
      }),
    ],
    [canEditDrivers, deleteDriver]
  );

  return (
    <>
      <SimpleTableWithSorting columns={columns} data={drivers} />
    </>
  );
};

DriversList.propTypes = {
  drivers: PropTypes.arrayOf(Shapes.userShape).isRequired,
  onDelete: PropTypes.func.isRequired,
  canEditDrivers: PropTypes.bool.isRequired,
};

DriversList.displayName = 'DriversList';

export default DriversList;
